// Empresas (/companies) — tela de plataforma, só para super admin.
// Mockup: docs/design/aeros-dark/mockups/Tela Aeros Dark - Empresas.dc.html
//
// Os dados vêm de `/companiesPlan` (todas as empresas, com plano e métricas
// de arquivos, sem paginação) e de `/sms/admin/saldos`. Como a lista chega
// inteira, os cinco KPIs e a paginação são calculados no cliente — o que a
// tela pagina é a exibição, não a busca.
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import SmsIcon from "@material-ui/icons/Sms";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import WorkspacePremiumIcon from "@material-ui/icons/CardMembership";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { useTheme, alpha } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import ForbiddenPage from "../../components/ForbiddenPage";
import CompanyEditModal from "../../components/CompanyEditModal";
import CompanyModal from "../../components/CompaniesModal";
import SmsCreditosModal from "../../components/SmsCreditosModal";
import LeadsCreditosModal from "../../components/LeadsCreditosModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import { useDate } from "../../hooks/useDate";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import useCompanyPageStyles, { TABLE_GRID } from "./companyPageStyles";
import { periodicidadeLabel, statusLabel as subscriptionStatusLabel } from "../../components/SubscriptionCard/subscriptionCardRules";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { recorrenciaLabel } from "../Plans/planRules";
import {
  companyKpis,
  companyState,
  statusLabel,
  dueHint,
  filtraEmpresas,
  formatBytes,
  formatBRL,
  initialsOf,
  parseAmount,
} from "./companyRules";

const POR_PAGINA = 12;

const TOM_POR_ESTADO = {
  active: { cor: AEROS.success, tone: "success" },
  soon: { cor: AEROS.warning, tone: "warning" },
  trial: { cor: AEROS.warning, tone: "warning" },
  expired: { cor: AEROS.error, tone: "error" },
  inactive: { cor: "#9CA3B8", tone: "neutral" },
};

const Companies = () => {
  const c = useCampaignPageStyles({ grid: TABLE_GRID });
  const classes = useCompanyPageStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { dateToClient, datetimeToClient } = useDate();
  const { remove } = useCompanies();

  const [companies, setCompanies] = useState([]);
  const [saldos, setSaldos] = useState({});
  const [loading, setLoading] = useState(true);
  const [termo, setTermo] = useState("");
  const [status, setStatus] = useState("all");
  const [pagina, setPagina] = useState(1);

  const [editId, setEditId] = useState(null);
  const [novaOpen, setNovaOpen] = useState(false);
  const [smsCompany, setSmsCompany] = useState(null);
  const [leadsCompany, setLeadsCompany] = useState(null);
  const [excluindo, setExcluindo] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [lista, saldosResp] = await Promise.all([
        api.get("/companiesPlan/"),
        api.get("/sms/admin/saldos").catch(() => ({ data: [] })),
      ]);
      setCompanies(lista.data?.companies || []);
      const mapa = {};
      (saldosResp.data || []).forEach((s) => {
        mapa[s.companyId] = s.smsSaldo;
      });
      setSaldos(mapa);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const kpis = useMemo(() => companyKpis(companies), [companies]);

  const filtradas = useMemo(
    () => filtraEmpresas(companies, { termo, status }),
    [companies, termo, status]
  );

  // Voltar para a primeira página ao filtrar: continuar na página 3 de uma
  // lista que agora tem 4 linhas mostraria a tabela vazia.
  useEffect(() => {
    setPagina(1);
  }, [termo, status]);

  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const visiveis = filtradas.slice(
    (paginaAtual - 1) * POR_PAGINA,
    paginaAtual * POR_PAGINA
  );

  const handleExcluir = async () => {
    if (!excluindo) return;
    try {
      await remove(excluindo.id);
      toast.success(i18n.t("compaies.toasts.deleted"));
      await carregar();
    } catch (err) {
      toastError(err);
    }
    setExcluindo(null);
  };

  if (!user?.super) {
    return <ForbiddenPage />;
  }

  const renderLinha = (company, index) => {
    const estado = companyState(company);
    const tom = TOM_POR_ESTADO[estado] || TOM_POR_ESTADO.active;
    const cor = AEROS.avatarCycle[index % AEROS.avatarCycle.length];
    const saldo = saldos[company.id];
    const valor = parseAmount(company?.plan?.amount);
    // A recorrência é do plano; `Companies.recurrence` é legado (só fallback).
    const recorrencia = company?.plan?.recurrence ?? company?.recurrence;

    return (
      <div
        key={company.id}
        className={`${c.tableRow} ${estado === "expired" ? classes.rowExpired : ""} ${
          estado === "inactive" ? classes.rowInactive : ""
        }`}
      >
        <div className={`${c.tableCell} ${classes.idCell}`}>#{company.id}</div>

        <div className={`${c.tableCell} ${classes.companyCell}`}>
          <div
            className={classes.companyAvatar}
            style={{ backgroundColor: alpha(cor, 0.16), color: cor }}
          >
            {initialsOf(company.name)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div className={classes.companyName} title={company.name}>
              {company.name}
            </div>
            <div className={classes.companyEmail} title={company.email}>
              {company.email}
            </div>
          </div>
        </div>

        <div className={c.tableCell}>
          <div className={classes.planName}>{company?.plan?.name || "—"}</div>
          <div className={classes.planValue}>
            {valor ? formatBRL(valor) : "—"}
            {recorrencia ? ` · ${recorrenciaLabel(recorrencia).toLowerCase()}` : ""}
          </div>
        </div>

        <div className={c.tableCell}>
          <span
            className={c.statusPill}
            style={{
              color: stateText(theme, tom.cor, tom.tone),
              backgroundColor: alpha(tom.cor, 0.12),
            }}
          >
            <span className={c.statusDot} style={{ backgroundColor: tom.cor }} />
            {statusLabel(estado)}
          </span>
        </div>

        <div className={`${c.tableCell} ${classes.dateCell}`}>
          {company.createdAt ? dateToClient(company.createdAt) : "—"}
        </div>

        <div className={c.tableCell}>
          <div
            className={`${classes.dueDate} ${
              estado === "expired" ? classes.dueDateLate : ""
            }`}
          >
            {company.dueDate ? dateToClient(company.dueDate) : "—"}
          </div>
          <div className={classes.dueHint}>{dueHint(company)}</div>
        </div>

        <div className={c.tableCell}>
          {(() => {
            const sub = company.subscription;
            const texto = sub
              ? `${periodicidadeLabel(sub)} · ${subscriptionStatusLabel(sub)}`
              : "—";
            return (
              <span className={classes.subscriptionText} title={texto}>
                {texto}
              </span>
            );
          })()}
        </div>

        <div className={`${c.tableCell} ${classes.dateCell}`}>
          {company.lastLogin ? datetimeToClient(company.lastLogin) : "—"}
        </div>

        <div className={c.tableCell}>
          <div className={classes.usageText}>
            {formatBytes(company?.metrics?.folderSize)} ·{" "}
            {i18n.t("aeros.companies.files", {
              count: company?.metrics?.numberOfFiles || 0,
            })}
          </div>
          <div className={classes.smsTag}>
            <SmsIcon />
            {saldo === undefined ? "—" : saldo} SMS
          </div>
        </div>

        <div className={c.cellActions}>
          <Tooltip title={i18n.t("aeros.companies.actions.edit")}>
            <IconButton
              className={c.rowActionButton}
              aria-label={i18n.t("aeros.companies.actions.edit")}
              onClick={() => setEditId(company.id)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n.t("aeros.companies.actions.sms")}>
            <IconButton
              className={c.rowActionButton}
              aria-label={i18n.t("aeros.companies.actions.sms")}
              onClick={() => setSmsCompany(company)}
            >
              <SmsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n.t("aeros.companies.actions.leads")}>
            <IconButton
              className={c.rowActionButton}
              aria-label={i18n.t("aeros.companies.actions.leads")}
              onClick={() => setLeadsCompany(company)}
            >
              <PeopleAltIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n.t("aeros.companies.actions.delete")}>
            <span>
              <IconButton
                className={`${c.rowActionButton} ${c.rowActionButtonDanger}`}
                aria-label={i18n.t("aeros.companies.actions.delete")}
                onClick={() => setExcluindo(company)}
                disabled={company.id === 1}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
    );
  };

  return (
    <MainContainer>
      <CompanyEditModal
        open={Boolean(editId)}
        companyId={editId}
        onClose={() => setEditId(null)}
        onSaved={carregar}
      />
      <CompanyModal
        open={novaOpen}
        onClose={() => {
          setNovaOpen(false);
          carregar();
        }}
      />
      <SmsCreditosModal
        open={Boolean(smsCompany)}
        company={smsCompany}
        onClose={() => {
          setSmsCompany(null);
          carregar();
        }}
      />
      <LeadsCreditosModal
        open={Boolean(leadsCompany)}
        company={leadsCompany}
        onClose={() => {
          setLeadsCompany(null);
          carregar();
        }}
      />
      <AerosConfirmModal
        open={Boolean(excluindo)}
        onClose={() => setExcluindo(null)}
        onConfirm={handleExcluir}
        icon={<DeleteOutlineIcon />}
        tone="danger"
        title={i18n.t("aeros.companies.confirm.title", {
          name: excluindo?.name || "",
        })}
        description={i18n.t("aeros.companies.confirm.message", {
          size: formatBytes(excluindo?.metrics?.folderSize),
        })}
        confirmLabel={i18n.t("aeros.companies.confirm.action")}
        typedWord={excluindo?.name}
        typedHint={i18n.t("aeros.companies.confirm.typedHint")}
      />

      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>
            {i18n.t("compaies.title")}{" "}
            <span className={c.pageTitleCount}>· {companies.length}</span>
          </div>
          <div className={classes.headerSubtitle}>
            {i18n.t("aeros.companies.subtitle")}
          </div>
        </div>

        <div className={c.headerActions}>
          <InputBase
            className={c.searchPill}
            placeholder={i18n.t("aeros.companies.searchPlaceholder")}
            type="search"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("aeros.companies.searchPlaceholder") }}
          />
          <TextField
            select
            size="small"
            variant="outlined"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={classes.statusFilter}
            style={{ width: 168 }}
            SelectProps={{ "aria-label": i18n.t("aeros.companies.filterStatus") }}
          >
            <MenuItem value="all">
              {i18n.t("aeros.companies.filter.all")}
            </MenuItem>
            <MenuItem value="active">{statusLabel("active")}</MenuItem>
            <MenuItem value="soon">{statusLabel("soon")}</MenuItem>
            <MenuItem value="trial">{statusLabel("trial")}</MenuItem>
            <MenuItem value="expired">{statusLabel("expired")}</MenuItem>
            <MenuItem value="inactive">{statusLabel("inactive")}</MenuItem>
          </TextField>
          <Button
            className={c.outlineButton}
            startIcon={<WorkspacePremiumIcon />}
            onClick={() => history.push("/plans")}
          >
            {i18n.t("aeros.companies.plansButton")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={c.gradientButton}
            startIcon={<AddIcon />}
            onClick={() => setNovaOpen(true)}
          >
            {i18n.t("aeros.companies.newCompany")}
          </Button>
        </div>
      </div>

      <div className={c.pageBody}>
        <div className={classes.kpiGrid}>
          <div className={classes.kpiCard}>
            <div className={classes.kpiLabel}>{i18n.t("compaies.title")}</div>
            <div className={classes.kpiValue}>{kpis.total}</div>
            <div className={classes.kpiHint}>
              {i18n.t("aeros.companies.kpi.activeInactive", {
                active: kpis.ativas,
                inactive: kpis.inativas,
              })}
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={classes.kpiLabel}>
              {i18n.t("aeros.companies.kpi.mrr")}
            </div>
            <div className={`${classes.kpiValue} ${classes.kpiSuccess}`}>
              {formatBRL(kpis.mrr, 0)}
            </div>
            <div className={classes.kpiHint}>
              {i18n.t("aeros.companies.kpi.mrrHint")}
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={classes.kpiLabel}>
              {i18n.t("aeros.companies.kpi.overdue")}
            </div>
            <div className={`${classes.kpiValue} ${classes.kpiError}`}>
              {kpis.vencidas}
            </div>
            <div className={classes.kpiHint}>
              {i18n.t("aeros.companies.kpi.overdueHint", {
                value: formatBRL(kpis.valorEmAtraso, 0),
              })}
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={classes.kpiLabel}>
              {i18n.t("aeros.companies.kpi.trial")}
            </div>
            <div className={`${classes.kpiValue} ${classes.kpiWarning}`}>
              {kpis.trial}
            </div>
            <div className={classes.kpiHint}>
              {i18n.t("aeros.companies.kpi.trialHint", {
                count: kpis.trialExpirando,
              })}
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={classes.kpiLabel}>
              {i18n.t("aeros.companies.kpi.storage")}
            </div>
            <div className={`${classes.kpiValue} ${classes.kpiInfo}`}>
              {formatBytes(kpis.bytes)}
            </div>
            <div className={classes.kpiHint}>
              {i18n.t("aeros.companies.files", { count: kpis.arquivos })}
            </div>
          </div>
        </div>

        <div className={c.tableCard}>
         <div className={classes.tableScroll}>
          <div className={classes.tableInner}>
          <div className={c.tableHeadRow}>
            <div className={c.tableHeadCell}>{i18n.t("compaies.table.ID")}</div>
            <div className={c.tableHeadCell}>
              {i18n.t("aeros.companies.table.company")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("aeros.companies.table.planValue")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("aeros.companies.table.status")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("compaies.table.createdAt")}
            </div>
            <div className={c.tableHeadCell}>{i18n.t("compaies.table.dueDate")}</div>
            <div className={c.tableHeadCell}>
              {i18n.t("aeros.companies.subscription.column")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("compaies.table.lastLogin")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("aeros.companies.table.usage")}
            </div>
            <div className={c.cellActionsHead}>
              {i18n.t("aeros.companies.table.actions")}
            </div>
          </div>

          {loading ? (
            <div className={c.loadingRow}>{i18n.t("aeros.companies.loading")}</div>
          ) : visiveis.length === 0 ? (
            <div className={c.emptyState}>
              <div className={c.emptyTitle}>
                {i18n.t("aeros.companies.emptyTitle")}
              </div>
              <div className={c.emptyText}>
                {i18n.t("aeros.companies.emptyText")}
              </div>
            </div>
          ) : (
            visiveis.map(renderLinha)
          )}
          </div>
         </div>

          {!loading && filtradas.length > 0 && (
            <div className={classes.tableFooter}>
              {i18n.t("aeros.companies.showing", {
                shown: visiveis.length,
                total: filtradas.length,
              })}
              {totalPaginas > 1 && (
                <div className={classes.pagination}>
                  <IconButton
                    className={classes.pageArrow}
                    disabled={paginaAtual === 1}
                    onClick={() => setPagina(paginaAtual - 1)}
                    aria-label={i18n.t("aeros.companies.prevPage")}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((n) => (
                    <Button
                      key={n}
                      className={`${classes.pageButton} ${
                        n === paginaAtual ? classes.pageButtonActive : ""
                      }`}
                      onClick={() => setPagina(n)}
                      aria-current={n === paginaAtual ? "page" : undefined}
                    >
                      {n}
                    </Button>
                  ))}
                  <IconButton
                    className={classes.pageArrow}
                    disabled={paginaAtual === totalPaginas}
                    onClick={() => setPagina(paginaAtual + 1)}
                    aria-label={i18n.t("aeros.companies.nextPage")}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={classes.footNote}>
          <InfoOutlinedIcon />
          {i18n.t("aeros.companies.legend")}
        </div>

      </div>
    </MainContainer>
  );
};

export default Companies;
